import { Chat, Message, isClientTeamChat, normaliseChatType } from '../../types/messaging';
import { formatTime } from '../../utils/dateFormat';
import { calendarDaysAgo, formatChatListTime } from '../../utils/listTime';
import { initialsFor } from '../../shared-ui/messages/conversationAvatar';

// Moved to utils/listTime so the native app can use them; re-exported for
// existing imports.
export { calendarDaysAgo, formatChatListTime };
import { DataService } from '../../services/DataService';
import type { MessageWorkflowState } from '../../types/database';

/** Auth stores user UUIDs uppercase, the DB stores them lowercase. */
export const isSameUser = (a?: string | null, b?: string | null): boolean =>
    Boolean(a && b && a.toLowerCase() === b.toLowerCase());

/**
 * One-line text for a new-message notification. Media and file messages carry
 * no `content`, so without this they arrive as a sender name over a blank line.
 */
export const getMessageNotificationPreview = (msg: Partial<Message>): string => {
    switch (msg.message_type) {
        case 'audio': return 'Voice message';
        case 'image': return 'Photo';
        case 'video': return msg.content?.trim() || 'Video';
        case 'location': return 'Location';
        case 'file':
            return msg.attachment_mime === 'application/vnd.quantis.email+json'
                ? 'Email: ' + (msg.attachment_name || 'Email').replace(/\.email\.json$/i, '')
                : msg.attachment_name || 'Attachment';
    }
    const text = typeof msg.content === 'string' ? msg.content.trim() : '';
    if (text) return text.slice(0, 80);
    return msg.attachment_url || msg.attachment_name ? 'Sent an attachment' : 'New message';
};

export interface ChatLastActivity {
    /** Message-like shape for a preview renderer — null when the conversation
     *  genuinely has no messages. */
    message: Partial<Message> | null;
    /** Always set; falls back to the chat's `updated_at`. */
    timestamp: string;
    senderId: string | null;
}

/**
 * Resolve the last message to show on a chat-list row.
 *
 * Two sources carry it and neither is authoritative on its own: `/chat/list`
 * returns the denormalised `last_message_*` cache columns, while the realtime
 * channel hydrates `chat.lastMessage` and leaves those columns at their
 * last-fetch values. Prefer whichever is newer so rows are populated on first
 * paint *and* a socket-delivered message isn't masked by a stale cache row.
 */
export const getChatLastActivity = (chat: Chat): ChatLastActivity => {
    const live = chat.lastMessage;
    const liveAt = live?.created_at ?? null;
    const cachedAt = chat.last_message_at ?? null;
    // Parse rather than string-compare: the two sources carry different ISO
    // shapes (`+00:00` from Postgres, `Z` from the realtime payload).
    const liveMs = liveAt ? new Date(liveAt).getTime() : NaN;
    const cachedMs = cachedAt ? new Date(cachedAt).getTime() : NaN;

    if (live && liveAt && (Number.isNaN(cachedMs) || liveMs >= cachedMs)) {
        return { message: live, timestamp: liveAt, senderId: live.sender_id ?? null };
    }

    // `last_message_at` falls back to the chat's `updated_at` server-side, so
    // the timestamp alone can't separate an empty conversation from one whose
    // last message was an attachment with no caption. Any other cache column
    // being set is the real signal — `last_message_id` on its own isn't enough,
    // since the route also serves rows from a message-table fallback that
    // leaves the id null.
    const hasMessage = Boolean(
        chat.last_message_id || chat.last_message_preview || chat.last_message_sender,
    );

    if (cachedAt && hasMessage) {
        return {
            message: {
                // The DB trigger stores a human-readable label here, so it is
                // already display-ready — treat it as plain text rather than
                // re-deriving a label from the message type.
                content: chat.last_message_preview || 'Attachment',
                message_type: 'text',
            },
            timestamp: cachedAt,
            senderId: chat.last_message_sender ?? null,
        };
    }

    return { message: null, timestamp: chat.updated_at, senderId: null };
};

// Shared with the native app's conversation row.
export const getFirstLastInitials = (name: string) => initialsFor(name);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuidLike = (value?: string | null) => Boolean(value && UUID_PATTERN.test(value.trim()));

const resolveProjectDisplay = (projectId?: string, fallbackProjectName?: string) => {
    if (!projectId) {
        return {
            projectName: fallbackProjectName?.trim() || undefined,
            projectCode: undefined as string | undefined,
        };
    }

    const project = DataService.getProject(projectId);
    const projectName = project?.name?.trim() || fallbackProjectName?.trim() || undefined;

    // projectCode is retained in the metadata shape for legacy callers, but we
    // no longer source it from the project record (removed RICS reference system).
    return {
        projectName,
        projectCode: undefined as string | undefined,
    };
};

const getWorkspaceId = (value: any): string | null => {
    if (!value) return null;
    return (
        value.workspace_id ||
        value.workspaceId ||
        value.company_id ||
        value.companyId ||
        null
    );
};

export interface ChatMetadata {
    name: string;
    avatar?: string;
    initials: string;
    projectId?: string;
    projectName?: string;
    projectCode?: string;
    isCommercial?: boolean;
    isExternal?: boolean;
    isSharedChannel?: boolean;
    externalParticipantCount?: number;
    badgeLabel?: string;
    conversationKind?: 'project_internal' | 'project_client' | 'commercial' | 'direct' | 'group';
    primaryBadge?: string;
    secondaryBadge?: string;
    attentionState?: 'needs_reply' | 'unread' | 'quiet';
    followUpCount?: number;
    pinnedDecisionCount?: number;
    savedCount?: number;
    /** Role of the other participant in a direct chat, e.g. "supervisor", "client" */
    otherRole?: string;
    /** Number of participants (excluding self) for group chats */
    participantCount?: number;
}

export interface ChatInboxState {
    hasUnread: boolean;
    unreadCount: number;
    followUpCount: number;
    pinnedDecisionCount: number;
    savedCount: number;
    needsReply: boolean;
    rankScore: number;
    lastActivityTimestamp: number;
}

export type MessageWorkflowStatesByChat = Record<string, MessageWorkflowState>;

const getCompanyId = (value: any): string | null => {
    if (!value) return null;
    return (
        value.company_id ||
        value.companyId ||
        value.user_metadata?.company_id ||
        value.user_metadata?.companyId ||
        null
    );
};

export const getCompanyName = (value: any): string | null => {
    const embeddedName = value?.company_name ?? value?.companyName;
    if (typeof embeddedName === 'string' && embeddedName.trim()) {
        return embeddedName.trim();
    }
    const companyId = getCompanyId(value);
    if (!companyId) return null;
    const company = DataService.getCompany(companyId);
    const name = company?.name?.trim();
    return name ? name : null;
};

export const getUserMessageWorkflowStates = (userId?: string | null): MessageWorkflowStatesByChat => {
    if (!userId) return {};
    try {
        return DataService.getUser(userId)?.preferences?.messageWorkflowStates || {};
    } catch (error) {
        console.error('[messages/utils] Failed to read message workflow states:', error);
        return {};
    }
};

export const getChatInboxState = (
    chat: Chat,
    user: any,
    workflowStates?: MessageWorkflowStatesByChat,
): ChatInboxState => {
    const hasUnread = Boolean(chat.unreadCount && chat.unreadCount > 0);
    const unreadCount = chat.unreadCount || 0;
    const workflowState = workflowStates?.[chat.id];
    const followUpCount = Array.isArray(workflowState?.followUpMessageIds) ? workflowState.followUpMessageIds.length : 0;
    const pinnedDecisionCount = Array.isArray(workflowState?.pinnedDecisionIds) ? workflowState.pinnedDecisionIds.length : 0;
    const savedCount = Array.isArray(workflowState?.savedMessageIds) ? workflowState.savedMessageIds.length : 0;
    const currentWorkspace = getWorkspaceId(user);
    const others = chat.participants?.filter(p => !isSameUser(p.user_id, user?.id)) || [];
    const hasExternalParticipants = others.some((participant) => {
        const participantWorkspace = getWorkspaceId(participant.user);
        return Boolean(participantWorkspace && currentWorkspace && participantWorkspace !== currentWorkspace);
    });
    const needsReply = followUpCount > 0 || (hasUnread && hasExternalParticipants);
    // `updated_at` alone would mis-order the inbox: the last-message trigger
    // writes the cache columns without touching it.
    const lastActivityTimestamp = new Date(getChatLastActivity(chat).timestamp || 0).getTime() || 0;
    const rankScore =
        (needsReply ? 1000 : 0) +
        (followUpCount * 100) +
        (pinnedDecisionCount * 25) +
        (savedCount * 5) +
        (hasUnread ? 50 : 0) +
        unreadCount;

    return {
        hasUnread,
        unreadCount,
        followUpCount,
        pinnedDecisionCount,
        savedCount,
        needsReply,
        rankScore,
        lastActivityTimestamp,
    };
};

export const sortChatsForProjectInbox = (
    chats: Chat[],
    user: any,
    workflowStates?: MessageWorkflowStatesByChat,
) => {
    return [...chats].sort((left, right) => {
        const leftState = getChatInboxState(left, user, workflowStates);
        const rightState = getChatInboxState(right, user, workflowStates);
        if (rightState.rankScore !== leftState.rankScore) {
            return rightState.rankScore - leftState.rankScore;
        }
        return rightState.lastActivityTimestamp - leftState.lastActivityTimestamp;
    });
};

export const getChatMetadata = (chat: Chat, user: any, workflowStates?: MessageWorkflowStatesByChat): ChatMetadata => {
    if (!user || !chat.participants) return { name: 'Unknown', avatar: undefined, initials: '??' } as ChatMetadata;

    const currentWorkspace = getWorkspaceId(user);
    // Drop blank user_ids — some chats carry a null-user row that would
    // otherwise survive the self-filter and inflate the participant count.
    // (Duplicate user_ids can't occur: chat_participants is keyed on
    // (chat_id, user_id).)
    const others = chat.participants.filter(p => p.user_id && !isSameUser(p.user_id, user.id));
    const externalOthers = others.filter((participant) => {
        const participantWorkspace = getWorkspaceId(participant.user);
        if (!participantWorkspace || !currentWorkspace) return false;
        return participantWorkspace !== currentWorkspace;
    });
    // An explicit type wins over the participant count: a chat created as a
    // 1-to-1 stays direct even if a stray participant row inflates the count,
    // and only an actual group/multi-party chat is group-like.
    const isGroupLike = normaliseChatType(chat.type) === 'channel';
    
    const inboxState = getChatInboxState(chat, user, workflowStates);
    const hasUnread = inboxState.hasUnread;
    const sharedInboxMetadata = {
        followUpCount: inboxState.followUpCount,
        pinnedDecisionCount: inboxState.pinnedDecisionCount,
        savedCount: inboxState.savedCount,
    };

    // Support both legacy "Project Thread • ID • Name" and new "#Name • ID" formats
    const oldProjectMatch = typeof chat.name === 'string' ? chat.name.match(/^Project Thread • ([^•]+) • (.+)$/) : null;
    const newProjectMatch = typeof chat.name === 'string' ? chat.name.match(/^#(.+?) • ([^•]+)$/) : null;
    
    // Display name carries no '#' prefix — the stored chat.name keeps its
    // '#Name • id' encoding, but every surface shows the plain project name
    // (the list row and chat header used to strip it locally; the Active
    // Conversations row didn't, leaking the '#').
    const projectThreadDisplayName = newProjectMatch
        ? newProjectMatch[1].trim()
        : (oldProjectMatch ? oldProjectMatch[2].trim() : null);
    const projectId = newProjectMatch
        ? newProjectMatch[2].trim()
        : (oldProjectMatch ? oldProjectMatch[1].trim() : undefined);
    const parsedProjectName = newProjectMatch
        ? newProjectMatch[1].trim()
        : (oldProjectMatch ? oldProjectMatch[2].trim() : undefined);
    const { projectName, projectCode } = resolveProjectDisplay(projectId, parsedProjectName);

    const isSharedBySchema =
        (chat.permitted_workspaces && chat.permitted_workspaces.length > 1) ||
        (chat.shared_with_orgs && chat.shared_with_orgs.length > 0);
    const isSharedChannel = isSharedBySchema || externalOthers.length > 0;
    const sharedBadge = isSharedChannel ? 'Shared' : (projectThreadDisplayName ? 'Project' : undefined);

    // ── COMMERCIAL CHAT ───────────────────────────────────────────────────────
    // Checked BEFORE isGroupLike: commercial chats now include all workspace
    // members so others.length > 1, which would otherwise send them into the
    // group branch and expose the workspace name to clients.
    // Only match by client_team — never by name, since "Commercial" is a generic name
    // used by other chat types (e.g. internal support channels).
    const isCommercial = isClientTeamChat(chat);
    if (isCommercial) {
        if (
          user.role === 'workspace_owner' || user.role === 'workspace_admin' || user.role === 'workspace_user' ||
          user.role === 'member'          || user.role === 'client'
        ) {
            return {
                name: 'Commercial',
                avatar: undefined,
                initials: 'CG',
                isCommercial: true,
                isSharedChannel,
                externalParticipantCount: externalOthers.length,
                badgeLabel: sharedBadge,
                primaryBadge: 'Commercial',
                secondaryBadge: hasUnread ? 'Unread' : undefined,
                conversationKind: 'commercial',
                attentionState: inboxState.needsReply ? 'needs_reply' : (hasUnread ? 'unread' : 'quiet'),
                ...sharedInboxMetadata,
            };
        } else {
            // For staff/admin: use chat.name (set to the workspace company name
            // at creation time), falling back to participant-derived company name.
            const chatNameFromRecord = (chat.name || '').trim();
            const clientParticipant = chat.participants.find(p =>
              p.user?.role === 'workspace_owner' || p.user?.role === 'workspace_admin' || p.user?.role === 'workspace_user' ||
              p.user?.role === 'member'          || p.user?.role === 'client'
            );
            const clientCompanyName = getCompanyName(clientParticipant?.user);
            const name = (chatNameFromRecord && chatNameFromRecord !== 'Commercial')
                ? chatNameFromRecord
                : (clientCompanyName || 'Commercial Chat');
            return {
                name,
                avatar: undefined,
                initials: getFirstLastInitials(name),
                isCommercial: true,
                isSharedChannel,
                externalParticipantCount: externalOthers.length,
                badgeLabel: sharedBadge,
                primaryBadge: 'Commercial',
                secondaryBadge: externalOthers.length > 0 ? 'Client' : undefined,
                conversationKind: 'commercial',
                attentionState: inboxState.needsReply ? 'needs_reply' : (hasUnread ? 'unread' : 'quiet'),
                participantCount: others.length,
                ...sharedInboxMetadata,
            };
        }
    }

    // ── GROUP CHAT ────────────────────────────────────────────────────────────
    // Must be checked BEFORE the generic "find other participant" fallback,
    // otherwise we'd always return the first participant (e.g. John Smith).
    if (isGroupLike) {
        const explicitName = projectThreadDisplayName || (chat.name || '').trim();
        const name = explicitName || 'Group Chat';
        // Build an abbreviated participant list for display (e.g. "Alice, Bob +2")
        const firstNames = others
            .filter(p => p.user?.name)
            .map(p => p.user!.name.split(' ')[0]);  // first names only
        const preview = firstNames.length > 0
            ? firstNames.slice(0, 2).join(', ') + (firstNames.length > 2 ? ` +${firstNames.length - 2}` : '')
            : name;
        return {
            name: explicitName || preview || 'Group Chat',
            avatar: undefined,
            initials: getFirstLastInitials(explicitName || 'GC'),
            projectId,
            projectName,
            projectCode,
            isSharedChannel,
            externalParticipantCount: externalOthers.length,
            badgeLabel: sharedBadge,
            primaryBadge: projectThreadDisplayName ? 'Project' : 'Group',
            secondaryBadge: externalOthers.length > 0 ? 'External' : undefined,
            // project_* kinds require an actual project link. A non-project group
            // that merely has an external member is still a plain 'group' (it must
            // show in the Groups section, not be hidden as a project chat).
            conversationKind: projectId
              ? (externalOthers.length > 0 ? 'project_client' : 'project_internal')
              : 'group',
            attentionState: inboxState.needsReply ? 'needs_reply' : (hasUnread ? 'unread' : 'quiet'),
            participantCount: others.length,
            ...sharedInboxMetadata,
        };
    }

    // ── DIRECT CHAT ───────────────────────────────────────────────────────────
    // For a true 1-to-1 chat this is always the correct "other" person.
    const otherParticipant = chat.participants.find(p => !isSameUser(p.user_id, user.id))?.user;

    if (otherParticipant) {
        const name = otherParticipant.name || otherParticipant.email || '?';
        const otherWorkspace = getWorkspaceId(otherParticipant);
        const isExternal = Boolean(currentWorkspace && otherWorkspace && otherWorkspace !== currentWorkspace);
        return {
            name,
            avatar: otherParticipant.avatar,
            initials: getFirstLastInitials(name),
            projectId,
            projectName,
            projectCode,
            isExternal,
            isSharedChannel: isSharedChannel || isExternal,
            externalParticipantCount: isExternal ? 1 : 0,
            badgeLabel: isExternal ? 'External' : sharedBadge,
            primaryBadge: isExternal ? 'Client' : 'Direct',
            secondaryBadge: hasUnread ? 'Unread' : undefined,
            conversationKind: 'direct',
            attentionState: inboxState.needsReply ? 'needs_reply' : (hasUnread ? 'unread' : 'quiet'),
            otherRole: (otherParticipant as any).jobTitle || (otherParticipant as any).job_title || (otherParticipant as any).position || undefined,
            ...sharedInboxMetadata,
        };
    }

    // ── SELF-CHAT / UNKNOWN ───────────────────────────────────────────────────
    // A chat explicitly typed 'direct' whose other participant row is missing
    // (a data gap — e.g. the second chat_participants row was never written)
    // reaches here with no resolvable "other". It must still live in Direct
    // Messages, not be dumped into Groups: classify by the explicit chat.type
    // rather than defaulting everything unknown to a group.
    const isDirectByType = normaliseChatType(chat.type) === 'direct';
    // Resilience for a missing participant row (data gap, or one side left the DM):
    // fall back to the counterparty resolved server-side (`counterparty_name`, the
    // latest non-self sender) or a realtime-hydrated `lastMessage.sender`, so the
    // chat shows a real name instead of "Direct message".
    // Only trust the realtime sender when it isn't the current user, and take the
    // name AND avatar from that one object so they can never disagree. The
    // server's `counterparty_name` carries no avatar, so the chat falls back to
    // initials in that case rather than borrowing the wrong person's avatar.
    const realtimeOther = chat.lastMessage?.sender && chat.lastMessage.sender.id !== user.id
        ? chat.lastMessage.sender
        : undefined;
    const fallbackSenderName = chat.counterparty_name || realtimeOther?.name || undefined;
    const name = chat.name || fallbackSenderName || (isDirectByType ? 'Direct message' : 'Chat');
    return {
        name,
        avatar: realtimeOther?.avatar,
        initials: getFirstLastInitials(name),
        projectId,
        projectName,
        projectCode,
        isSharedChannel,
        externalParticipantCount: externalOthers.length,
        badgeLabel: sharedBadge,
        primaryBadge: projectThreadDisplayName ? 'Project' : (isDirectByType ? 'Direct' : 'Chat'),
        secondaryBadge: hasUnread ? 'Unread' : undefined,
        conversationKind: isDirectByType ? 'direct' : (projectThreadDisplayName ? 'project_internal' : 'group'),
        attentionState: inboxState.needsReply ? 'needs_reply' : (hasUnread ? 'unread' : 'quiet'),
        ...sharedInboxMetadata,
    };
};


/**
 * Human-friendly time labels:
 * - Today → 09:35
 * - Yesterday
 * - This week → Mon/Tue...
 * - Else → Jan 5
 */
export const formatRelativeTime = (value?: string | Date) => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '';

    const diffDays = calendarDaysAgo(date);

    if (diffDays === 0) {
        return formatTime(date);
    }
    if (diffDays === 1) {
        return 'Yesterday';
    }
    if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};


/**
 * Check if a message can be edited by the current user
 * - Must be sent by the user
 * - Must be within 15 minutes of creation
 */
export const canEditMessage = (message: any, user: any) => {
    if (!user || !message) return false;
    if (message.sender_id !== user.id) return false;
    
    // Allow editing within 15 minutes
    const createdAt = new Date(message.created_at);
    const now = new Date();
    const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    
    return diffMinutes < 15;
};
